import { useState } from "react";
import { Download, BarChart3 } from "lucide-react";
import { formatDate } from "../lib/api";
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, AreaChart, Area,
  PieChart, Pie, Cell, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";

const COLORS = ["#C84B31", "#1A1A18", "#2F6F4E", "#8A2526", "#C89B3C", "#4A6FA5", "#6B4E9E", "#68665E"];

function isDate(v) { return typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v); }
export function fmtCell(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (isDate(v)) return formatDate(v);
  if (typeof v === "number") return v.toLocaleString("en-IN", { maximumFractionDigits: 2 });
  return String(v);
}

export function ChartView({ chart, columns, rows }) {
  const x = chart.x && columns.includes(chart.x) ? chart.x : columns[0];
  const y = chart.y && columns.includes(chart.y) ? chart.y : columns.find((c) => c !== x && rows.some((r) => typeof r[c] === "number")) || columns[1];
  const data = rows.map((r) => ({ ...r, [x]: fmtCell(r[x]) }));
  if (!x || !y) return <div className="text-sm text-[#68665E]">Not enough data to chart.</div>;
  const t = chart.type;
  return (
    <ResponsiveContainer width="100%" height={300}>
      {t === "pie" ? (
        <PieChart>
          <Pie data={data} dataKey={y} nameKey={x} cx="50%" cy="50%" outerRadius={100} label>
            {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
          </Pie>
          <Tooltip /><Legend />
        </PieChart>
      ) : t === "line" ? (
        <LineChart data={data}><CartesianGrid strokeDasharray="3 3" stroke="#EEE" /><XAxis dataKey={x} tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} /><Tooltip /><Line type="monotone" dataKey={y} stroke="#C84B31" strokeWidth={2} /></LineChart>
      ) : t === "area" ? (
        <AreaChart data={data}><CartesianGrid strokeDasharray="3 3" stroke="#EEE" /><XAxis dataKey={x} tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} /><Tooltip /><Area type="monotone" dataKey={y} stroke="#C84B31" fill="#C84B31" fillOpacity={0.2} /></AreaChart>
      ) : (
        <BarChart data={data}><CartesianGrid strokeDasharray="3 3" stroke="#EEE" /><XAxis dataKey={x} tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} /><Tooltip /><Bar dataKey={y} fill="#C84B31" /></BarChart>
      )}
    </ResponsiveContainer>
  );
}

export function AiResult({ columns, rows, chart, rowCount, exportUrl, defaultView }) {
  const initial = defaultView === "table" ? "table" : (defaultView === "chart" && chart) ? "chart" : (chart ? "chart" : "table");
  const [tab, setTab] = useState(initial);
  return (
    <div className="border border-[#E5E3DB] bg-white" data-testid="result-block">
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#E5E3DB]">
        <div className="flex gap-1">
          <button onClick={() => setTab("table")} className={`px-2 py-1 text-xs ${tab === "table" ? "bg-[#1A1A18] text-white" : "text-[#68665E]"}`} data-testid="tab-table">Table</button>
          {chart && <button onClick={() => setTab("chart")} className={`px-2 py-1 text-xs inline-flex items-center gap-1 ${tab === "chart" ? "bg-[#1A1A18] text-white" : "text-[#68665E]"}`} data-testid="tab-chart"><BarChart3 className="w-3 h-3" />Chart</button>}
        </div>
        {exportUrl && (
          <a href={exportUrl} target="_blank" rel="noreferrer"
            className="text-xs inline-flex items-center gap-1 text-[#C84B31] hover:underline" data-testid="download-excel-btn">
            <Download className="w-3.5 h-3.5" /> Excel
          </a>
        )}
      </div>
      {tab === "table" ? (
        <div className="overflow-auto max-h-80">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-[#F5F3EC]"><tr>{columns.map((c) => <th key={c} className="text-left px-3 py-2 font-semibold text-[#1A1A18] whitespace-nowrap">{c}</th>)}</tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-t border-[#F0EDE5]" data-testid={`result-row-${i}`}>
                  {columns.map((c) => <td key={c} className="px-3 py-1.5 whitespace-nowrap text-[#3A3A34]">{fmtCell(r[c])}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length === 0 && <div className="px-3 py-6 text-center text-sm text-[#68665E]">No matching records.</div>}
          {rowCount > rows.length && <div className="px-3 py-2 text-xs text-[#68665E]">Showing {rows.length} of {rowCount} rows{exportUrl ? " — download Excel for all." : "."}</div>}
        </div>
      ) : (
        <div className="p-3"><ChartView chart={chart} columns={columns} rows={rows} /></div>
      )}
    </div>
  );
}
